/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useEffect, useRef } from 'react';

/* Shapes */
import * as d3 from 'd3';
import { MeterArc, MeterArcProps, ArcColorProps } from './components/MeterArc';
import { MeterNeedle, MeterNeedleProps } from './components/MeterNeedle';
import { Dimensions } from 'interfaces';
import { useState } from 'react';
import { Colors } from '@blueprintjs/core';
import { useWidgetControlsContext } from 'views/common/WidgetControls';

const defaultArcConfig = {
	arcRadii: { inner: 0, outer: 0 },
	colors: { fullFill: false, interpolator: undefined, reverse: false, score: 0, segments: 0 },
	delimitingDegreeAngles: { start: 0, end: 0 },
	position: { x: 0, y: 0 },
};

const defaultNeedleConfig = {
	delimitingDegreeAngles: { start: 0, end: 0 },
	meterPercent: 0,
	needleHeight: 0,
	needleRadius: 0,
	position: { x: 0, y: 0 },
};

/**
 *
 * @param dimensions Dimensions of the SVG element container.
 * 	The lower value of width/height will be used to determine arc radius.
 * 	Values may be modified to accommodate start/end angles
 * @param dimensions.width The width of the SVG container element.
 * @param dimensions.height The height of the SVG container element.
 * @param widgetConfiguration Configuration values for the meter
 * @param widgetConfiguration.data Rating object that contains a percentile score and the denominator score was derived from.
 * @param [widgetConfiguration.colors] Color data to use for coloring/shading meter
 * @param [widgetConfiguration.needlePosition='center'] String value indicating which radius level the needle points to on the arc.
 * @param [widgetConfiguration.arcWidth] The desired width of the arc.
 * @returns
 */
export const Meter: React.FC<MeterProps> = ({
	dimensions: { width, height },
	widgetConfiguration: { data, colors, needlePosition = 'center', arcWidth = 50, labels },
}) => {
	const svgRef = useRef<SVGSVGElement>(null);
	const [arcConfig, setArcConfig] = useState<MeterArcProps>(defaultArcConfig);
	const [needleConfig, setNeedleConfig] = useState<MeterNeedleProps>(defaultNeedleConfig);

	const {dispatch} = useWidgetControlsContext();
	useEffect(() => {
		//use widget controls context to hide the labels toggle switch
		dispatch && dispatch({type: "setLabelsToggleBtnStatus", payload: false});
	}, []);

	useEffect(() => {
		const newArcConfig = defaultArcConfig;
		const newNeedleConfig = defaultNeedleConfig;

		const svgWidth = svgRef.current.clientWidth;
		const svgHeight = svgRef.current.clientHeight;
		const minDimension = Math.min(svgWidth / 2, svgHeight);
		const padding = 25;

		const outer = minDimension - padding;
		const inner = outer - arcWidth;
		const midArc = (outer + inner) / 2;
		/* Arc */
		newArcConfig.arcRadii = { inner, outer };
		newArcConfig.colors = {
			fullFill: true,
			interpolator: d3.interpolateRdYlGn,
			reverse: false,
			score: data.score,
			segments: 5,
			...colors,
		};
		newArcConfig.position = { x: svgWidth / 2, y: svgHeight - padding / 2 };
		setArcConfig((prevConfig) => ({ ...prevConfig, ...newArcConfig }));

		/* Needle */
		newNeedleConfig.delimitingDegreeAngles = newArcConfig.delimitingDegreeAngles;
		newNeedleConfig.meterPercent = data.score;
		newNeedleConfig.needleHeight = needlePosition === 'center' ? midArc : newArcConfig.arcRadii[needlePosition];
		newNeedleConfig.needleRadius = Math.min(10, inner - 10);
		newNeedleConfig.position = newArcConfig.position;
		setNeedleConfig((prevConfig) => ({ ...prevConfig, ...newNeedleConfig }));
	}, [arcWidth, colors, data, needlePosition, width, height, setArcConfig]);

	return (
		<div css={meterContainer}>
			<svg ref={svgRef} width="100%" height="100%">
				<MeterArc {...arcConfig} />

				<MeterNeedle {...needleConfig} />
			</svg>
			<div css={labelContainer}>
				<div css={rangeLabel}>{labels.left}</div>
				<div className="dataLabel" css={dataLabel} style={{ width: arcConfig.arcRadii.inner * 2 }}>
					<div css={dataNumber}>{Number(data.score * data.denominator).toFixed(0)}</div>
					<div>{labels.data}</div>
				</div>
				<div css={rangeLabel}>{labels.right}</div>
			</div>
		</div>
	);
};

const meterContainer = css`
	height: 100%;
	display: grid;
	grid-template-rows: 1fr auto;
`;

const labelContainer = css`
	display: flex;
	justify-content: center;
	text-align: center;
	color: ${Colors.GRAY3};
	font-weight: 600;
`;

const rangeLabel = css``;

const dataLabel = css`
	display: flex;
	flex-direction: column;
`;

const dataNumber = css`
	color: ${Colors.DARK_GRAY5};
	font-size: 3.5em;
	font-weight: 900;
`;

export interface Rating {
	score: number;
	denominator: number;
}

export interface MeterProps {
	dimensions: Dimensions;
	widgetConfiguration: {
		data: Rating;
		colors?: ArcColorProps;
		needlePosition?: 'inner' | 'center' | 'outer';
		arcWidth?: number;
		labels: { left: string; data: string; right: string };
	};
}
